import React, { useCallback, useEffect, useMemo, useState } from "react";
import cx from "classnames";
import { Input } from "reactstrap";
import { debounce } from "lodash-es";
import { useTranslate } from "hooks";
import s from "../_ProductConfigurator/ProductConfiguratorComponent.module.scss";

export const InputCard = ({
  label,
  subLabel,
  value,
  displayValue,
  valueMin,
  valueMax,
  unit,
  isValueEditable,
  onChange,
  testId,
  validationMessage,
  validationType,
  defaultValue,
}) => {
  const translate = useTranslate();
  const [currentValue, setCurrentValue] = useState(value);
  const [isValid, setIsValid] = useState(true);

  const shouldBeValidate = useMemo(
    () => valueMin && valueMax,
    [valueMax, valueMin],
  );
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentValue(value);
    setIsValid(true);
  }, [value]);

  useEffect(() => {
    shouldBeValidate || shouldBeValidate === 0
      ? // eslint-disable-next-line react-hooks/set-state-in-effect
        setIsValid(
          (valueMin <= currentValue &&
            valueMax >= currentValue &&
            currentValue !== "") ||
            +currentValue === +defaultValue,
        )
      : setIsValid(true);
  }, [currentValue, defaultValue, shouldBeValidate, valueMax, valueMin]);

  const handleChange = useCallback(({ target }) => {
    setCurrentValue(target.value);
  }, []);

  const debounceOnChange = useMemo(
    () =>
      debounce((val) => {
        onChange(val);
      }, 500),
    [onChange],
  );

  const handleSend = useCallback(
    (e) => {
      let sendValue = e?.target?.value || currentValue;
      if (sendValue === `${defaultValue}`) {
        return;
      }

      if (sendValue === "" && defaultValue !== undefined) {
        sendValue = defaultValue;
        setCurrentValue(sendValue);
        return;
      }

      if (
        !isNaN(valueMin) &&
        valueMin !== null &&
        (sendValue < valueMin || sendValue === "")
      ) {
        sendValue = valueMin;
      }
      if (!isNaN(valueMax) && valueMax !== null && sendValue > valueMax) {
        sendValue = valueMax;
      }

      setCurrentValue(sendValue);
      if (+sendValue !== +value) {
        debounceOnChange(sendValue);
      }
    },
    [valueMin, valueMax, currentValue, value, debounceOnChange, defaultValue],
  );

  const handleInputKeyDown = useCallback(
    ({ key, keyCode }) => {
      if (key === "Enter" || keyCode === 13) {
        handleSend();
      }
    },
    [handleSend],
  );

  return (
    <div className="position-relative">
      <div className={s.cardInputRow}>
        <div
          className={cx(
            { "pb-0": !isValid || validationMessage },
            s.cardInputRowResponsive,
          )}
        >
          <div className="d-flex flex-column align-items-start justify-content-center font-md ps-2">
            {label && <div dangerouslySetInnerHTML={{ __html: label }} />}
            {subLabel && (
              <div
                className="text-secondary font14"
                dangerouslySetInnerHTML={{ __html: subLabel }}
              />
            )}
          </div>
          <div
            className={cx(
              "d-flex align-items-start justify-content-center flex-column",
              s.inputWrapper,
            )}
          >
            <div className="d-flex align-items-center">
              {isValueEditable ? (
                <Input
                  type="number"
                  value={currentValue}
                  invalid={!isValid}
                  onBlur={handleSend}
                  onChange={handleChange}
                  id={`input-feild-${testId}`}
                  className={cx(
                    "py-1 me-2 border border-gray-300 rounded-2 text-center",
                    { "border-danger": !isValid },
                    s.inputArea,
                  )}
                  data-testid={testId}
                  onKeyDown={handleInputKeyDown}
                />
              ) : (
                <span
                  className={cx(
                    "d-flex fs-md justify-content-center align-items-center bg-gray-100 me-2",
                    s.resultValue,
                  )}
                  data-testid={testId}
                >
                  {displayValue}
                </span>
              )}
              <div dangerouslySetInnerHTML={{ __html: unit }} />
            </div>
          </div>
        </div>
      </div>
      {isValueEditable && !isValid && (
        <div className="ps-2 mb-2 fs-sm text-danger">
          {translate("PRODUCT_VALUE_RANGE_WARNING", [valueMin, valueMax])}
        </div>
      )}
      {isValid && validationMessage && validationType !== 1 && (
        <div className="ps-2 mb-2 fs-sm text-warning">{validationMessage}</div>
      )}
    </div>
  );
};
